import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { bkAdmin, nok, errMsg } from "@/lib/booking";
import { http } from "@/lib/api";

const AGR = { forespurt: "Ny forespørsel", tilbud_sendt: "Tilbud sendt", aktiv: "Aktiv", pauset: "Pauset", avsluttet: "Avsluttet", avslatt: "Avslått" };
const FREQ = { ukentlig: "Hver uke", annenhver_uke: "Annenhver uke", hver_4_uke: "Hver 4. uke", annet: "Annet" };
const DAYS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];
const sel = "h-8 rounded border bg-background text-xs px-1";

const AgreementRow = ({ a, cleaners, reload }) => {
  const [e, setE] = useState({ status: a.status, price_per_visit: a.price_per_visit, duration_hours: a.duration_hours, frequency: a.frequency,
    weekday: a.weekday, time: a.time, start_date: a.start_date || "", cleaner_id: a.cleaner_id || "", admin_note: a.admin_note || "" });
  const set = (k, v) => setE({ ...e, [k]: v });
  const save = async () => {
    try {
      const body = { ...e, start_date: e.start_date || null, cleaner_id: e.cleaner_id || null };
      const r = await bkAdmin.updateAgreement(a.id, body);
      toast.success(`Lagret${r.visits_created ? ` – ${r.visits_created} besøk opprettet` : ""}${r.email_sent ? " – kunden er varslet" : r.email_sent === false ? " – e-post ikke sendt" : ""}`);
      if (r.conflicts?.length) toast.warning(`Ingen ledig kapasitet: ${r.conflicts.join(", ")}`);
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="rounded-xl border bg-card p-4 text-sm" data-testid={`agreement-${a.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="font-mono">{a.ref}</b><span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold">{AGR[a.status]}</span>
        <b>{a.name}</b><span className="text-muted-foreground">{a.phone} · {a.email}</span>
        <span className="ml-auto text-xs text-muted-foreground">{a.created_at?.slice(0, 10)}</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {a.premises} · {a.sqm} m² · {a.address}, {a.postcode} · ønsket {FREQ[a.frequency]} {a.frequency_note} {DAYS[a.weekday]} kl. {a.time} · rom: {a.rooms?.join(", ") || "-"} · {a.products === "kundens" ? "kundens produkter" : "vi tar med"}
        {a.allergies && <> · hensyn: {a.allergies}</>}{a.instructions && <> · «{a.instructions}»</>} · veiledende {nok(a.estimate?.total_per_visit)}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2 text-xs">
        <label>Status<select data-testid={`agr-status-${a.id}`} className={`${sel} block`} value={e.status} onChange={(x) => set("status", x.target.value)}>{Object.entries(AGR).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label>Pris/besøk<Input data-testid={`agr-price-${a.id}`} type="number" className="h-8 w-24" value={e.price_per_visit} onChange={(x) => set("price_per_visit", Number(x.target.value))} /></label>
        <label>Timer<Input type="number" step="0.5" className="h-8 w-20" value={e.duration_hours} onChange={(x) => set("duration_hours", Number(x.target.value))} /></label>
        <label>Frekvens<select className={`${sel} block`} value={e.frequency} onChange={(x) => set("frequency", x.target.value)}>{Object.entries(FREQ).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label>Dag<select className={`${sel} block`} value={e.weekday} onChange={(x) => set("weekday", Number(x.target.value))}>{DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></label>
        <label>Tid<Input type="time" className="h-8 w-24" value={e.time} onChange={(x) => set("time", x.target.value)} /></label>
        <label>Startdato<Input data-testid={`agr-start-${a.id}`} type="date" className="h-8 w-36" value={e.start_date} onChange={(x) => set("start_date", x.target.value)} /></label>
        <label>Renholder<select className={`${sel} block`} value={e.cleaner_id} onChange={(x) => set("cleaner_id", x.target.value)}><option value="">Automatisk</option>{cleaners.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
        <label className="flex-1 min-w-[160px]">Internt notat<Input className="h-8" value={e.admin_note} onChange={(x) => set("admin_note", x.target.value)} /></label>
        <Button size="sm" data-testid={`agr-save-${a.id}`} onClick={save}>Lagre</Button>
      </div>
      <div className={`mt-2 text-xs font-semibold ${a.profit?.unprofitable ? "text-red-600" : a.profit?.below_target ? "text-amber-600" : "text-green-700"}`}>
        {(a.profit?.unprofitable || a.profit?.below_target) && <AlertTriangle className="inline h-3 w-3 mr-1" />}Dekningsbidrag per besøk: {nok(a.profit?.margin)} ({a.profit?.margin_pct} %)
      </div>
      {a.upcoming?.length > 0 && <div className="mt-2 text-xs">Kommende besøk: {a.upcoming.map((u) => `${u.date} ${u.start}`).join(" · ")}</div>}
      {a.conflicts?.length > 0 && <div className="mt-1 text-xs text-red-600">Ikke planlagt pga. kapasitet: {a.conflicts.join(", ")}</div>}
    </div>
  );
};

export const Agreements = ({ cleaners }) => {
  const [list, setList] = useState(null);
  const load = () => bkAdmin.agreements().then(setList);
  useEffect(() => { load(); }, []);
  if (!list) return null;
  const groups = [["Nye forespørsler", (a) => ["forespurt", "tilbud_sendt"].includes(a.status)], ["Aktive avtaler", (a) => a.status === "aktiv"], ["Avsluttet / pauset", (a) => ["pauset", "avsluttet", "avslatt"].includes(a.status)]];
  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">Aktiv avtale oppretter besøk 3 måneder frem i tid (fylles på automatisk). Ingen betaling trekkes automatisk.</p>
      {groups.map(([t, f]) => (
        <div key={t}><h3 className="font-heading font-bold mb-2">{t} ({list.filter(f).length})</h3>
          <div className="space-y-3">{list.filter(f).map((a) => <AgreementRow key={a.id + a.status} a={a} cleaners={cleaners} reload={load} />)}</div></div>
      ))}
    </div>
  );
};

export const Codes = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ code: "", pct: 10, max_uses: 0, active: true });
  const load = () => bkAdmin.codes().then(setList);
  useEffect(() => { load(); }, []);
  const save = async (c) => { try { await bkAdmin.saveCode(c); toast.success("Rabattkode lagret"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border bg-card p-4 text-xs">
        <label>Kode<Input data-testid="code-input" className="h-8 w-36" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} /></label>
        <label>Rabatt %<Input data-testid="code-pct" type="number" className="h-8 w-20" value={f.pct} onChange={(e) => setF({ ...f, pct: Number(e.target.value) })} /></label>
        <label>Maks bruk (0 = ubegrenset)<Input type="number" className="h-8 w-24" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: Number(e.target.value) })} /></label>
        <Button size="sm" data-testid="code-save" onClick={() => save(f)}>Opprett</Button>
      </div>
      <p className="text-xs text-muted-foreground">Sjekk lønnsomhet før du gir rabatt. Maks 50 %.</p>
      <div className="rounded-xl border bg-card divide-y">
        {list.map((c) => (
          <div key={c.code} className="flex items-center gap-3 p-3 text-sm"><b className="font-mono">{c.code}</b><span>{c.pct} %</span>
            <span className="text-muted-foreground">brukt {c.used || 0}{c.max_uses ? `/${c.max_uses}` : ""}</span>
            <label className="ml-auto flex items-center gap-1 text-xs"><input type="checkbox" checked={c.active} onChange={(e) => save({ ...c, active: e.target.checked })} />Aktiv</label></div>
        ))}
        {!list.length && <p className="p-4 text-sm text-muted-foreground">Ingen rabattkoder.</p>}
      </div>
    </div>
  );
};

export const PfAdmin = () => {
  const [d, setD] = useState(null);
  const load = () => http.get("/credits/admin").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const c = d.config;
  const save = async () => { await http.put("/credits/admin/config", { standard_limit: c.standard_limit, free_limit: c.free_limit, pro_limit: c.pro_limit }); toast.success("Grenser lagret"); load(); };
  const upd = async (co, p) => { await http.put(`/credits/admin/company/${co.id}`, { pro: co.pro, add_credits: 0, ...p }); load(); };
  return (
    <div className="space-y-4" data-testid="pf-admin">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-card p-4 text-xs">
        {[["standard_limit", "Standard maks per søk"], ["free_limit", "Gratis-søk maks"], ["pro_limit", "Pro maks per søk (≤150)"]].map(([k, l]) => (
          <label key={k}>{l}<Input data-testid={`pf-${k}`} type="number" className="h-8 w-28" value={c[k]} onChange={(e) => setD({ ...d, config: { ...c, [k]: Number(e.target.value) } })} /></label>
        ))}
        <Button size="sm" data-testid="pf-save-config" onClick={save}>Lagre</Button>
      </div>
      <div className="rounded-xl border bg-card divide-y text-sm">
        {d.companies.map((co) => (
          <div key={co.id} className="flex flex-wrap items-center gap-3 p-3"><b className="flex-1">{co.name || co.email}</b>
            <span>{co.credits || 0} kreditter · {co.free_searches_left ?? 10} gratis</span>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!co.pro} onChange={(e) => upd(co, { pro: e.target.checked })} />Pro</label>
            <Button size="sm" variant="outline" onClick={() => upd(co, { add_credits: 10 })}>+10 kreditter</Button></div>
        ))}
      </div>
    </div>
  );
};

export const CustomersTab = () => {
  const [list, setList] = useState([]);
  useEffect(() => { bkAdmin.customers().then(setList); }, []);
  return (
    <div className="overflow-x-auto rounded-xl border bg-card" data-testid="admin-customers">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-muted-foreground border-b"><tr>{["Navn", "E-post", "Telefon", "Adresse", "Oppdrag", "Omsetning", "Siste", "Konto"].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
        <tbody>{list.map((c) => (
          <tr key={c.email} className="border-b last:border-0"><td className="p-2 font-semibold">{c.name}</td><td className="p-2">{c.email}</td><td className="p-2">{c.phone}</td><td className="p-2">{c.address}</td>
            <td className="p-2">{c.bookings}</td><td className="p-2">{nok(c.revenue)}</td><td className="p-2">{c.last}</td><td className="p-2">{c.has_account ? "Ja" : "–"}</td></tr>
        ))}</tbody>
      </table>
      {!list.length && <p className="p-6 text-center text-sm text-muted-foreground">Ingen kunder ennå.</p>}
    </div>
  );
};

export const Messages = () => {
  const [emails, setEmails] = useState([]);
  const [contacts, setContacts] = useState([]);
  const load = () => { bkAdmin.emails().then(setEmails); bkAdmin.contacts().then(setContacts); };
  useEffect(() => { load(); }, []);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div><h3 className="font-heading font-bold mb-2">Kontaktmeldinger</h3>
        <div className="rounded-xl border bg-card divide-y">{contacts.map((c) => (
          <div key={c.id} className={`p-3 text-sm ${c.handled ? "opacity-50" : ""}`}><b>{c.name}</b> · {c.email} · {c.phone}<span className="float-right text-xs">{c.at.slice(0, 10)}</span>
            <p className="mt-1 whitespace-pre-wrap">{c.message}</p>
            {!c.handled && <Button size="sm" variant="outline" className="mt-2" onClick={() => bkAdmin.contactHandled(c.id).then(load)}>Marker som behandlet</Button>}</div>
        ))}{!contacts.length && <p className="p-4 text-sm text-muted-foreground">Ingen meldinger.</p>}</div></div>
      <div><h3 className="font-heading font-bold mb-2">E-postlogg (siste 200)</h3>
        <div className="rounded-xl border bg-card divide-y text-xs" data-testid="email-log">{emails.map((m, i) => (
          <div key={i} className="p-2 flex gap-2"><span className={m.status === "sendt" ? "text-green-700" : "text-red-600"}>{m.status}</span><span className="flex-1">{m.subject} → {m.to}</span><span>{m.at.slice(5, 16).replace("T", " ")}</span></div>
        ))}</div></div>
    </div>
  );
};

export const SeoGoogle = () => {
  const [s, setS] = useState(null);
  useEffect(() => { bkAdmin.settings().then(setS); }, []);
  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => { await bkAdmin.saveSettings({ site_url: s.site_url, ga4_id: s.ga4_id.trim(), gsc_verification: s.gsc_verification.trim(), org_nr: s.org_nr, visit_address: s.visit_address, phone: s.phone, email: s.email }); toast.success("Lagret"); };
  const base = (s.site_url || window.location.origin).replace(/\/$/, "");
  const f = (k, label, ph) => <label className="text-xs block">{label}<Input data-testid={`seo-${k}`} className="h-9 mt-1" placeholder={ph} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} /></label>;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border bg-card p-4 space-y-3">
        <h3 className="font-heading font-bold">Google og bedriftsinfo</h3>
        {f("site_url", "Nettadresse (eget domene)", "https://www.askstokke-renhold.no")}
        {f("ga4_id", "Google Analytics 4 måle-ID", "G-XXXXXXXXXX")}
        {f("gsc_verification", "Search Console verifiseringskode (meta-tag content)", "abc123...")}
        {f("org_nr", "Organisasjonsnummer (kun verifisert)", "")}
        {f("visit_address", "Besøksadresse (la stå tom hvis ingen)", "")}
        {f("phone", "Telefon", "")}{f("email", "E-post", "")}
        <Button data-testid="seo-save" onClick={save}>Lagre</Button>
      </div>
      <div className="rounded-xl border bg-card p-4 text-sm space-y-2">
        <h3 className="font-heading font-bold">Status</h3>
        <p>Google Analytics: <b>{s.ga4_id ? "ID lagt inn – måler kun etter samtykke. Kontroller i GA4 Sanntid." : "Ikke koblet til"}</b></p>
        <p>Search Console: <b>{s.gsc_verification ? "Kode lagt inn – verifiser i Search Console" : "Ikke verifisert"}</b></p>
        <p>Sitemap: <a className="underline" href={`${base}/sitemap.xml`} target="_blank" rel="noreferrer">{base}/sitemap.xml</a></p>
        <p>robots.txt: <a className="underline" href={`${base}/robots.txt`} target="_blank" rel="noreferrer">{base}/robots.txt</a></p>
        <p className="text-xs text-muted-foreground">Anbefalt: koble til eget domene, og verifiser i Search Console med DNS-oppføring (mest pålitelig for nettsider som denne). Sitemap og kanoniske lenker bruker domenet satt i REACT_APP_SITE_URL ved publisering. Målte hendelser: click_book_once, click_recurring, booking_submitted, agreement_requested, purchase.</p>
        <p className="text-xs text-muted-foreground">Google-bedriftsprofil: bruk bare navn, telefon, e-post og områder som stemmer. Uten besøksadresse velger du «serviceområde-bedrift».</p>
      </div>
    </div>
  );
};
